import { create } from 'zustand';
import type { LoyaltyDto, LoyaltyEntryDto } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';

/**
 * A customer's loyalty points (ADR-083): the total and what it is worth, the
 * ledger paged by cursor, and a change by hand with a reason.
 */

interface LoyaltyState {
  customerId: string | null;
  loyalty: LoyaltyDto | null;
  entries: LoyaltyEntryDto[];
  nextCursor: string | null;
  loading: boolean;
  saving: boolean;
  error: string | null;

  load: (customerId: string) => Promise<void>;
  loadMore: () => Promise<void>;
  adjust: (customerId: string, points: number, note: string) => Promise<boolean>;
}

const PAGE_SIZE = 20;

export const useLoyaltyStore = create<LoyaltyState>((set, get) => ({
  customerId: null,
  loyalty: null,
  entries: [],
  nextCursor: null,
  loading: false,
  saving: false,
  error: null,

  async load(customerId) {
    set({ customerId, loyalty: null, entries: [], nextCursor: null, loading: true, error: null });
    try {
      const [loyalty, page] = await Promise.all([
        withSession(() => adminApi.loyalty.get(customerId)),
        withSession(() => adminApi.loyalty.entries(customerId, { limit: PAGE_SIZE })),
      ]);
      // Another customer was opened while this one loaded.
      if (get().customerId !== customerId) return;
      set({ loyalty, entries: page.items, nextCursor: page.meta.nextCursor, loading: false });
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load the points.') });
    }
  },

  async loadMore() {
    const { customerId, nextCursor, loading } = get();
    if (!customerId || !nextCursor || loading) return;
    set({ loading: true });
    try {
      const page = await withSession(() => adminApi.loyalty.entries(customerId, { limit: PAGE_SIZE, cursor: nextCursor }));
      set((state) => ({ entries: [...state.entries, ...page.items], nextCursor: page.meta.nextCursor, loading: false }));
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load more movements.') });
    }
  },

  async adjust(customerId, points, note) {
    set({ saving: true, error: null });
    try {
      await withSession(() => adminApi.loyalty.adjust(customerId, { points, note }));
      set({ saving: false });
      await get().load(customerId);
      return true;
    } catch (error) {
      set({ saving: false, error: errorMessage(error, 'Could not change the points.') });
      return false;
    }
  },
}));
